'use client';

import { useMemo, useState } from 'react';
import {
    Activity,
    ChevronDown,
    ChevronRight,
    Clock,
    Mail,
    ShoppingCart,
    CreditCard,
    CheckCircle2,
    AlertCircle,
    MousePointer,
    Eye,
    RefreshCw,
    User,
    Truck,
    MapPin,
    DollarSign,
    Package,
    Link2,
    Globe,
    Footprints,
    FlaskConical,
    Split,
    Route,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { DateDisplay } from '@/components/date-display';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { trpc } from '@/lib/api/trpc';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { DeviceTypeIcon } from '@/components/ui/device-icons';
import {
    getEventLabel,
    getEventIconType,
    getEventCategoryConfig,
    type EventIconType,
} from '@/lib/constants/app-events';

interface CustomerEventTraceProps {
    customerId: string;
    className?: string;
}

/**
 * Extended event properties returned by the backend
 * These are extracted from event.properties in the SQL query
 */
interface ExtendedEventProperties {
    amount: number | null;
    currency: string | null;
    status: string | null;
    subStatus: string | null;
    itemCount: number | null;
    shippingRateId: string | null;
    shippingRateName: string | null;
    checkoutUrl: string | null;
    country: string | null;
    city: string | null;
    promotionCount: number | null;
    createdFrom: string | null;
    isPreview: boolean;
    // A/B test variant info
    variantId: string | null;
    variantLabel: string | null;
    // Step name (from properties, not stepType column)
    stepName: string | null;
}

/**
 * Render icon component based on icon type from centralized constants
 */
function EventIcon({ iconType, className }: { iconType: EventIconType; className?: string }) {
    const iconClass = cn('h-3.5 w-3.5', className);
    switch (iconType) {
        case 'credit-card':
            return <CreditCard className={cn(iconClass, 'text-green-600')} />;
        case 'shopping-cart':
            return <ShoppingCart className={cn(iconClass, 'text-blue-600')} />;
        case 'mail':
            return <Mail className={cn(iconClass, 'text-purple-600')} />;
        case 'mouse-pointer':
            return <MousePointer className={cn(iconClass, 'text-amber-600')} />;
        case 'eye':
            return <Eye className={cn(iconClass, 'text-sky-600')} />;
        case 'alert-circle':
            return <AlertCircle className={cn(iconClass, 'text-red-600')} />;
        case 'check-circle':
            return <CheckCircle2 className={cn(iconClass, 'text-green-600')} />;
        case 'user':
            return <User className={cn(iconClass, 'text-amber-600')} />;
        case 'truck':
            return <Truck className={cn(iconClass, 'text-blue-600')} />;
        case 'map-pin':
            return <MapPin className={cn(iconClass, 'text-blue-600')} />;
        default:
            return <Activity className={cn(iconClass, 'text-muted-foreground')} />;
    }
}

/**
 * Customer Event Trace Component
 * Shows full event timeline for a customer
 */
const ORPHAN_GROUP_KEY = '__no_funnel_session__';

type TraceEvent = inferRouterOutputs<AppRouter>['analytics']['customerTrace']['events'][number];

interface EventGroup {
    key: string;
    funnelSessionId: string | null;
    funnelId: string | null;
    events: TraceEvent[];
    firstAt: Date;
    lastAt: Date;
    hasPreview: boolean;
    variantLabel: string | null;
}

export function CustomerEventTrace({ customerId, className }: CustomerEventTraceProps) {
    const { t } = useTranslation('customers');
    const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
    const [collapsedSessions, setCollapsedSessions] = useState<Set<string>>(new Set());

    // Fetch customer trace
    const {
        data: traceData,
        isLoading,
        refetch,
        isFetching,
    } = trpc.analytics.customerTrace.useQuery(
        {
            customerId,
            limit: 200,
            // Include draft events (preview/staging) - they're only created locally
            excludeDraft: false,
        },
        {
            staleTime: 30000,
        }
    );

    // Toggle event expansion
    const toggleEvent = (eventId: string) => {
        setExpandedEvents((prev) => {
            const next = new Set(prev);
            if (next.has(eventId)) {
                next.delete(eventId);
            } else {
                next.add(eventId);
            }
            return next;
        });
    };

    const toggleSession = (key: string) => {
        setCollapsedSessions((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    // Group events by funnel session, preserving chronological order (DESC) within each group
    const eventGroups = useMemo<EventGroup[]>(() => {
        const events = traceData?.events ?? [];
        const groups = new Map<string, EventGroup>();

        for (const event of events) {
            const key = event.funnelSessionId || ORPHAN_GROUP_KEY;
            const at = new Date(event.createdAt);
            const ext = event as TraceEvent & ExtendedEventProperties;
            const existing = groups.get(key);
            if (!existing) {
                groups.set(key, {
                    key,
                    funnelSessionId: event.funnelSessionId,
                    funnelId: event.funnelId,
                    events: [event],
                    firstAt: at,
                    lastAt: at,
                    hasPreview: Boolean(ext.isPreview),
                    variantLabel: ext.variantLabel || (ext.variantId ? ext.variantId.split('_').pop() ?? null : null),
                });
                continue;
            }
            existing.events.push(event);
            if (at < existing.firstAt) existing.firstAt = at;
            if (at > existing.lastAt) existing.lastAt = at;
            if (ext.isPreview) existing.hasPreview = true;
            if (!existing.variantLabel && (ext.variantLabel || ext.variantId)) {
                existing.variantLabel = ext.variantLabel || (ext.variantId ? ext.variantId.split('_').pop() ?? null : null);
            }
            if (!existing.funnelId && event.funnelId) existing.funnelId = event.funnelId;
        }

        return Array.from(groups.values()).sort((a, b) => b.lastAt.getTime() - a.lastAt.getTime());
    }, [traceData?.events]);

    if (isLoading) {
        return (
            <Card className={cn('flex flex-col', className)}>
                <CardHeader className="flex-shrink-0 px-4 py-3 border-b">
                    <Skeleton className="h-5 w-32" />
                </CardHeader>
                <CardContent className="flex-1 p-4 space-y-3">
                    {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="h-10 w-full rounded-md" />
                    ))}
                </CardContent>
            </Card>
        );
    }

    if (!traceData) {
        return (
            <Card className={cn('flex flex-col', className)}>
                <CardHeader className="flex-shrink-0 px-4 py-3 border-b">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Activity className="h-4 w-4" />
                        {t('eventTrace.title')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 p-4">
                    <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
                        {t('eventTrace.empty')}
                    </div>
                </CardContent>
            </Card>
        );
    }

    const { events, summary, totalEvents, firstEventAt, funnelSessions, checkoutSessions } = traceData;

    return (
        <Card className={cn('flex flex-col', className)}>
            {/* Header */}
            <CardHeader className="flex-shrink-0 px-4 py-3 border-b">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Activity className="h-4 w-4" />
                        {t('eventTrace.title')}
                        <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-normal">
                            {totalEvents}
                        </Badge>
                    </CardTitle>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => refetch()}
                        disabled={isFetching}
                    >
                        <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
                    </Button>
                </div>
            </CardHeader>

            {/* Summary Section */}
            <div className="flex-shrink-0 px-4 py-3 border-b bg-muted/20 space-y-2">
                {/* Stats Row */}
                <div className="flex flex-wrap items-center gap-2 text-xs">
                    {firstEventAt && (
                        <span className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {formatDistanceToNow(new Date(firstEventAt), { addSuffix: true })}
                        </span>
                    )}
                    {funnelSessions.length > 0 && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal">
                            {funnelSessions.length} funnel{funnelSessions.length > 1 ? 's' : ''}
                        </Badge>
                    )}
                    {checkoutSessions.length > 0 && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 font-normal">
                            {checkoutSessions.length} checkout{checkoutSessions.length > 1 ? 's' : ''}
                        </Badge>
                    )}
                </div>

                {/* Event Type Summary */}
                {summary.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                        {summary.slice(0, 6).map((s) => (
                            <Badge
                                key={s.eventType}
                                variant="secondary"
                                className="text-[10px] h-5 px-1.5 font-normal"
                            >
                                {getEventLabel(s.eventType)}: {s.count}
                            </Badge>
                        ))}
                        {summary.length > 6 && (
                            <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-normal">
                                +{summary.length - 6}
                            </Badge>
                        )}
                    </div>
                )}
            </div>

            {/* Event Timeline - Scrollable, grouped by funnel session */}
            <CardContent className="flex-1 overflow-y-auto p-0">
                {eventGroups.map((group) => {
                    const isCollapsed = collapsedSessions.has(group.key);
                    const isOrphan = group.key === ORPHAN_GROUP_KEY;
                    const shortSession = group.funnelSessionId ? group.funnelSessionId.slice(-8) : null;
                    const shortFunnel = group.funnelId ? group.funnelId.slice(-8) : null;

                    return (
                        <div key={group.key} className="border-b border-border last:border-b-0">
                            <button
                                type="button"
                                onClick={() => toggleSession(group.key)}
                                className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    {isCollapsed ? (
                                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                    ) : (
                                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                    )}
                                    <Route className="h-3.5 w-3.5 text-violet-600 flex-shrink-0" />
                                    {isOrphan ? (
                                        <span className="text-xs font-medium text-muted-foreground">{t('eventTrace.otherEvents')}</span>
                                    ) : (
                                        <>
                                            <span className="text-xs font-medium">{t('eventTrace.funnelSession')}</span>
                                            <code className="text-[10px] bg-background px-1.5 py-0.5 rounded border">
                                                {shortSession}
                                            </code>
                                            {shortFunnel && (
                                                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                                    · {t('eventTrace.funnel')}{' '}
                                                    <code className="text-[10px] bg-background px-1.5 py-0.5 rounded border">
                                                        {shortFunnel}
                                                    </code>
                                                </span>
                                            )}
                                        </>
                                    )}
                                    <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-normal">
                                        {group.events.length}
                                    </Badge>
                                    {group.hasPreview && (
                                        <Badge
                                            variant="yellow"
                                            className="h-4 gap-0.5 px-1 text-[9px] font-normal flex-shrink-0"
                                        >
                                            <FlaskConical className="h-2.5 w-2.5" />
                                            Preview
                                        </Badge>
                                    )}
                                    {group.variantLabel && (
                                        <Badge
                                            variant="purple"
                                            className="h-4 gap-0.5 px-1 text-[9px] font-normal flex-shrink-0"
                                        >
                                            <Split className="h-2.5 w-2.5" />
                                            {group.variantLabel}
                                        </Badge>
                                    )}
                                </div>
                                <span className="flex items-center gap-1 text-[10px] text-muted-foreground flex-shrink-0">
                                    <Clock className="h-3 w-3" />
                                    {formatDistanceToNow(group.lastAt, { addSuffix: true })}
                                </span>
                            </button>

                            {!isCollapsed && (
                                <div className="relative ml-6 border-l border-border py-1">
                                    {group.events.map((event) => {
                                        // Extended properties from backend (extracted from event.properties)
                                        const ext = event as typeof event & ExtendedEventProperties;
                        const categoryConfig = getEventCategoryConfig(event.eventType);
                        const iconType = getEventIconType(event.eventType);
                        const isExpanded = expandedEvents.has(event.id);
                        const hasDetails = event.stepId || event.funnelSessionId || event.checkoutSessionId || event.orderId || 
                            ext.amount || ext.status || ext.shippingRateId || ext.itemCount || ext.checkoutUrl;

                        return (
                            <div key={event.id} className="relative">
                                {/* Timeline dot */}
                                <div className="absolute -left-[5px] top-3.5 w-2 h-2 rounded-full bg-background border-2 border-muted-foreground/40" />

                                <Collapsible open={isExpanded} onOpenChange={() => hasDetails && toggleEvent(event.id)}>
                                    <CollapsibleTrigger asChild>
                                        <div
                                            className={cn(
                                                'flex items-center justify-between py-2 pl-4 pr-4 transition-colors',
                                                hasDetails && 'cursor-pointer hover:bg-muted/30',
                                                isExpanded && 'bg-muted/20'
                                            )}
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                {hasDetails ? (
                                                    isExpanded ? (
                                                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                                    ) : (
                                                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                                    )
                                                ) : (
                                                    <div className="w-3.5" />
                                                )}
                                                <EventIcon iconType={iconType} />
                                                <span className="text-xs font-medium truncate">
                                                    {getEventLabel(event.eventType)}
                                                    {/* Show step name for funnel events (prefer stepName over stepType) */}
                                                    {(event.stepId && (ext.stepName || event.stepType)) && (
                                                        <span className="text-muted-foreground font-normal">
                                                            → {ext.stepName || event.stepType}
                                                        </span>
                                                    )}
                                                </span>
                                                <Badge
                                                    variant="outline"
                                                    className={cn('text-[9px] h-4 px-1 flex-shrink-0', categoryConfig.bgColor, categoryConfig.color, categoryConfig.borderColor)}
                                                >
                                                    {categoryConfig.label}
                                                </Badge>
                                                {ext.isPreview && (
                                                    <Badge variant="yellow" className="h-4 gap-0.5 px-1 text-[9px] font-normal flex-shrink-0">
                                                        <FlaskConical className="h-2.5 w-2.5" />
                                                        Preview
                                                    </Badge>
                                                )}
                                                {ext.variantId && (
                                                    <Badge variant="purple" className="h-4 gap-0.5 px-1 text-[9px] font-normal flex-shrink-0">
                                                        <Split className="h-2.5 w-2.5" />
                                                        {ext.variantLabel || ext.variantId.split('_').pop() || 'Variant'}
                                                    </Badge>
                                                )}
                                            </div>
                                            <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">
                                                <DateDisplay value={event.createdAt} format="MMM d, HH:mm" />
                                            </span>
                                        </div>
                                    </CollapsibleTrigger>

                                    {hasDetails && (
                                        <CollapsibleContent>
                                            <div className="ml-10 mr-4 mb-2 p-2.5 bg-muted/30 rounded-md text-[11px] space-y-1.5 border border-border/50">
                                                {/* Amount & Currency */}
                                                {ext.amount != null && (
                                                    <div className="flex items-center gap-2">
                                                        <DollarSign className="h-3 w-3 text-green-600 flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.amount')}</span>
                                                        <span className="font-medium tabular-nums">
                                                            {(ext.amount / 100).toFixed(2)} {ext.currency || ''}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Status */}
                                                {ext.status && (
                                                    <div className="flex items-center gap-2">
                                                        <Activity className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.status')}</span>
                                                        <Badge 
                                                            variant={ext.status === 'paid' || ext.status === 'completed' ? 'green' : 
                                                                     ext.status === 'failed' || ext.status === 'declined' ? 'red' : 
                                                                     ext.status === 'pending' ? 'yellow' : 'secondary'}
                                                            className="text-[9px] h-4 px-1"
                                                        >
                                                            {ext.status}
                                                        </Badge>
                                                        {ext.subStatus && ext.subStatus !== ext.status && (
                                                            <span className="text-muted-foreground">({ext.subStatus})</span>
                                                        )}
                                                    </div>
                                                )}
                                                {/* Order ID */}
                                                {event.orderId && (
                                                    <div className="flex items-center gap-2">
                                                        <Package className="h-3 w-3 text-blue-600 flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.order')}</span>
                                                        <code className="text-[10px] bg-background px-1.5 py-0.5 rounded border">{event.orderId}</code>
                                                    </div>
                                                )}
                                                {/* Payment ID */}
                                                {event.paymentId && (
                                                    <div className="flex items-center gap-2">
                                                        <CreditCard className="h-3 w-3 text-purple-600 flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.payment')}</span>
                                                        <code className="text-[10px] bg-background px-1.5 py-0.5 rounded border">{event.paymentId}</code>
                                                    </div>
                                                )}
                                                {/* Checkout Session */}
                                                {event.checkoutSessionId && (
                                                    <div className="flex items-center gap-2">
                                                        <ShoppingCart className="h-3 w-3 text-orange-600 flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.checkout')}</span>
                                                        <code className="text-[10px] bg-background px-1.5 py-0.5 rounded border">{event.checkoutSessionId.slice(-8)}</code>
                                                    </div>
                                                )}
                                                {/* Item Count */}
                                                {ext.itemCount != null && (
                                                    <div className="flex items-center gap-2">
                                                        <Package className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.items')}</span>
                                                        <span className="font-medium">{ext.itemCount}</span>
                                                    </div>
                                                )}
                                                {/* Shipping Rate */}
                                                {ext.shippingRateId && (
                                                    <div className="flex items-center gap-2">
                                                        <Truck className="h-3 w-3 text-blue-600 flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.shipping')}</span>
                                                        <span className="font-medium">{ext.shippingRateName || ext.shippingRateId}</span>
                                                    </div>
                                                )}
                                                {/* Checkout URL */}
                                                {ext.checkoutUrl && (
                                                    <div className="flex items-center gap-2">
                                                        <Link2 className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.url')}</span>
                                                        <a 
                                                            href={ext.checkoutUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 hover:underline truncate max-w-[200px]"
                                                        >
                                                            {ext.checkoutUrl.replace(/^https?:\/\//, '').slice(0, 40)}...
                                                        </a>
                                                    </div>
                                                )}
                                                {/* Step Info */}
                                                {event.stepId && (
                                                    <div className="flex items-center gap-2">
                                                        <Footprints className="h-3 w-3 text-amber-600 flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.step')}</span>
                                                        <span className="font-medium">{event.stepType || event.stepId}</span>
                                                    </div>
                                                )}
                                                {/* Funnel Session */}
                                                {event.funnelSessionId && (
                                                    <div className="flex items-center gap-2">
                                                        <Activity className="h-3 w-3 text-violet-600 flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.session')}</span>
                                                        <code className="text-[10px] bg-background px-1.5 py-0.5 rounded border">{event.funnelSessionId.slice(-8)}</code>
                                                    </div>
                                                )}
                                                {/* Location */}
                                                {(ext.country || ext.city) && (
                                                    <div className="flex items-center gap-2">
                                                        <Globe className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.location')}</span>
                                                        <span>{[ext.city, ext.country].filter(Boolean).join(', ')}</span>
                                                    </div>
                                                )}
                                                {/* Device */}
                                                {event.deviceType && (
                                                    <div className="flex items-center gap-2">
                                                        <DeviceTypeIcon deviceType={event.deviceType} className="h-3 w-3 flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.device')}</span>
                                                        <span className="flex items-center gap-1.5">
                                                            {event.deviceType}
                                                            {event.browser && <span className="text-muted-foreground">/ {event.browser}</span>}
                                                            {event.os && <span className="text-muted-foreground">/ {event.os}</span>}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Email */}
                                                {event.email && (
                                                    <div className="flex items-center gap-2">
                                                        <Mail className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.email')}</span>
                                                        <span>{event.email}</span>
                                                    </div>
                                                )}
                                                {/* Created From */}
                                                {ext.createdFrom && (
                                                    <div className="flex items-center gap-2">
                                                        <Activity className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                                        <span className="text-muted-foreground w-20">{t('eventTrace.source')}</span>
                                                        <Badge variant="outline" className="text-[9px] h-4 px-1">{ext.createdFrom}</Badge>
                                                    </div>
                                                )}
                                            </div>
                                        </CollapsibleContent>
                                    )}
                                </Collapsible>
                            </div>
                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </CardContent>

            {/* Footer - Show more indicator */}
            {totalEvents > events.length && (
                <div className="flex-shrink-0 px-4 py-2 border-t bg-muted/10">
                    <p className="text-xs text-muted-foreground text-center">
                        {t('eventTrace.showingEvents', { shown: events.length, total: totalEvents })}
                    </p>
                </div>
            )}
        </Card>
    );
}

export default CustomerEventTrace;

